import { buildCsvDocument } from './csvUtils';

export const TAX_CLASS_OPTIONS = [
  { value: '', label: '-' },
  { value: '1', label: 'Klasse 1' },
  { value: '2', label: 'Klasse 2' },
  { value: '3', label: 'Klasse 3' },
  { value: '4', label: 'Klasse 4' },
  { value: '5', label: 'Klasse 5' },
  { value: '6', label: 'Klasse 6' },
];

export const RELIGION_OPTIONS = [
  { value: '', label: '-' },
  { value: 'none', label: 'Keine' },
  { value: 'rk', label: 'Röm.-katholisch' },
  { value: 'ev', label: 'Evangelisch' },
  { value: 'jd', label: 'Jüdisch' },
  { value: 'islam', label: 'Islamisch' },
  { value: 'other', label: 'Sonstige' },
];

export const MARITAL_STATUS_OPTIONS = [
  { value: '', label: '-' },
  { value: 'single', label: 'Ledig' },
  { value: 'married', label: 'Verheiratet' },
  { value: 'divorced', label: 'Geschieden' },
  { value: 'widowed', label: 'Verwitwet' },
  { value: 'civil_union', label: 'Lebenspartnerschaft' },
];

export const EMPLOYMENT_TYPE_OPTIONS = [
  { value: '', label: '-' },
  { value: 'full_time', label: 'Vollzeit' },
  { value: 'part_time', label: 'Teilzeit' },
];

export const WORK_AREA_OPTIONS = [
  { value: '', label: '-' },
  { value: 'office', label: 'Büro' },
  { value: 'field', label: 'Außendienst' },
];

export const COMPENSATION_TYPE_OPTIONS = [
  { value: 'hourly', label: 'Stundenlohn' },
  { value: 'salary', label: 'Fixum' },
];

export const TAX_CLASS_VALUE_SET = new Set(TAX_CLASS_OPTIONS.map((x) => x.value));
export const RELIGION_VALUE_SET = new Set(RELIGION_OPTIONS.map((x) => x.value));
export const MARITAL_STATUS_VALUE_SET = new Set(MARITAL_STATUS_OPTIONS.map((x) => x.value));
export const EMPLOYMENT_TYPE_VALUE_SET = new Set(EMPLOYMENT_TYPE_OPTIONS.map((x) => x.value));
export const WORK_AREA_VALUE_SET = new Set(WORK_AREA_OPTIONS.map((x) => x.value));
export const COMPENSATION_TYPE_VALUE_SET = new Set(COMPENSATION_TYPE_OPTIONS.map((x) => x.value));

export type StammdatenExportFieldKind =
  | 'date'
  | 'number'
  | 'text'
  | 'license'
  | 'boolean'
  | 'tax_class'
  | 'religion'
  | 'marital_status'
  | 'employment_type'
  | 'work_area'
  | 'compensation_type';

export type StammdatenExportFieldKey =
  | 'first_name'
  | 'last_name'
  | 'birth_date'
  | 'birth_name'
  | 'birth_place'
  | 'birth_country'
  | 'street'
  | 'house_number'
  | 'postal_code'
  | 'city'
  | 'phone'
  | 'email'
  | 'department'
  | 'position'
  | 'work_area'
  | 'employment_type'
  | 'weekly_hours'
  | 'statutory_vacation_days'
  | 'additional_vacation_days'
  | 'start_date'
  | 'end_date'
  | 'expected_hours_per_day'
  | 'driving_license_classes'
  | 'bank_account_holder'
  | 'iban'
  | 'bank_name'
  | 'personal_number'
  | 'social_security_number'
  | 'tax_id'
  | 'tax_class'
  | 'religion_code'
  | 'health_insurance_name'
  | 'child_allowance_count'
  | 'marital_status'
  | 'nationality_code'
  | 'severe_disability'
  | 'compensation_type'
  | 'hourly_wage'
  | 'monthly_salary'
  | 'allowance_per_hour'
  | 'vma_tax_free_per_day'
  | 'overnight_tax_free_per_day'
  | 'emergency_contact_name'
  | 'emergency_contact_relation'
  | 'emergency_contact_phone';

export type StammdatenExportField = {
  key: StammdatenExportFieldKey;
  label: string;
  kind: StammdatenExportFieldKind;
};

export type StammdatenExportGroup = {
  id: string;
  title: string;
  fields: StammdatenExportField[];
};

export const ZERO_MEANS_EMPTY_NUMBER_FIELDS = new Set<StammdatenExportFieldKey>([
  'weekly_hours',
  'statutory_vacation_days',
  'additional_vacation_days',
  'hourly_wage',
  'monthly_salary',
  'allowance_per_hour',
  'vma_tax_free_per_day',
  'overnight_tax_free_per_day',
  'child_allowance_count',
]);

export const EMPLOYEE_STAMMDATEN_EXPORT_GROUPS: StammdatenExportGroup[] = [
  {
    id: 'personal',
    title: 'Persönliche Daten',
    fields: [
      { key: 'first_name', label: 'Vorname', kind: 'text' },
      { key: 'last_name', label: 'Nachname', kind: 'text' },
      { key: 'birth_date', label: 'Geburtsdatum', kind: 'date' },
      { key: 'birth_name', label: 'Geburtsname', kind: 'text' },
      { key: 'birth_place', label: 'Geburtsort', kind: 'text' },
      { key: 'birth_country', label: 'Geburtsland', kind: 'text' },
      { key: 'street', label: 'Straße', kind: 'text' },
      { key: 'house_number', label: 'Hausnummer', kind: 'text' },
      { key: 'postal_code', label: 'PLZ', kind: 'text' },
      { key: 'city', label: 'Ort', kind: 'text' },
      { key: 'phone', label: 'Telefon', kind: 'text' },
      { key: 'email', label: 'E-Mail', kind: 'text' },
    ],
  },
  {
    id: 'job',
    title: 'Beruf',
    fields: [
      { key: 'personal_number', label: 'Personalnummer', kind: 'text' },
      { key: 'department', label: 'Abteilung', kind: 'text' },
      { key: 'position', label: 'Position', kind: 'text' },
      { key: 'work_area', label: 'Arbeitsbereich', kind: 'work_area' },
      { key: 'employment_type', label: 'Beschäftigungsart', kind: 'employment_type' },
      { key: 'weekly_hours', label: 'Wöchentliche Arbeitsstunden', kind: 'number' },
      { key: 'statutory_vacation_days', label: 'Gesetzlicher Urlaub', kind: 'number' },
      { key: 'additional_vacation_days', label: 'Zusätzlicher Urlaub', kind: 'number' },
      { key: 'start_date', label: 'Eintrittsdatum', kind: 'date' },
      { key: 'end_date', label: 'Austrittsdatum', kind: 'date' },
      { key: 'expected_hours_per_day', label: 'Sollstunden/Tag', kind: 'number' },
      { key: 'driving_license_classes', label: 'Führerscheinklassen', kind: 'license' },
    ],
  },
  {
    id: 'finance',
    title: 'Finanzen & Steuer',
    fields: [
      { key: 'bank_account_holder', label: 'Kontoinhaber', kind: 'text' },
      { key: 'iban', label: 'IBAN', kind: 'text' },
      { key: 'bank_name', label: 'Bank', kind: 'text' },
      { key: 'social_security_number', label: 'SV-Nummer', kind: 'text' },
      { key: 'tax_id', label: 'Steuer-ID', kind: 'text' },
      { key: 'tax_class', label: 'Steuerklasse', kind: 'tax_class' },
      { key: 'religion_code', label: 'Konfession', kind: 'religion' },
    ],
  },
  {
    id: 'reporting',
    title: 'Meldedaten',
    fields: [
      { key: 'health_insurance_name', label: 'Krankenkasse', kind: 'text' },
      { key: 'child_allowance_count', label: 'Kinderfreibetrag', kind: 'number' },
      { key: 'marital_status', label: 'Familienstand', kind: 'marital_status' },
      { key: 'nationality_code', label: 'Staatsangehörigkeit', kind: 'text' },
      { key: 'severe_disability', label: 'Schwerbehinderung', kind: 'boolean' },
    ],
  },
  {
    id: 'compensation',
    title: 'Vergütung',
    fields: [
      { key: 'compensation_type', label: 'Vergütungsart', kind: 'compensation_type' },
      { key: 'hourly_wage', label: 'Stundenlohn', kind: 'number' },
      { key: 'monthly_salary', label: 'Monatliches Fixum', kind: 'number' },
      { key: 'allowance_per_hour', label: 'Zulage pro Stunde', kind: 'number' },
      { key: 'vma_tax_free_per_day', label: 'Steuerfreie VMA pro Tag', kind: 'number' },
      { key: 'overnight_tax_free_per_day', label: 'Steuerfreie ÜN-Pauschale/Tag', kind: 'number' },
    ],
  },
  {
    id: 'emergency',
    title: 'Notfallkontakt',
    fields: [
      { key: 'emergency_contact_name', label: 'Name', kind: 'text' },
      { key: 'emergency_contact_relation', label: 'Beziehung', kind: 'text' },
      { key: 'emergency_contact_phone', label: 'Telefon', kind: 'text' },
    ],
  },
];

const FIELD_BY_KEY = new Map<StammdatenExportFieldKey, StammdatenExportField>();
for (const group of EMPLOYEE_STAMMDATEN_EXPORT_GROUPS) {
  for (const field of group.fields) {
    FIELD_BY_KEY.set(field.key, field);
  }
}

export const ALL_STAMMDATEN_EXPORT_FIELD_KEYS: StammdatenExportFieldKey[] = EMPLOYEE_STAMMDATEN_EXPORT_GROUPS.flatMap(
  (g) => g.fields.map((f) => f.key),
);

export const STAMMDATEN_EXPORT_FIELD_KEY_SET = new Set<string>(ALL_STAMMDATEN_EXPORT_FIELD_KEYS);

export type EmployeeExportRow = Record<string, unknown>;

export type StammdatenSectionField = {
  label: string;
  key: StammdatenExportFieldKey;
  type?: 'date' | 'number';
  kind?: StammdatenExportFieldKind | 'array';
};

export function toStammdatenSectionFields(
  group: StammdatenExportGroup,
): Array<{
  label: string;
  key: StammdatenExportFieldKey;
  type?: 'date' | 'number';
  kind?: 'license' | 'tax_class' | 'religion' | 'marital_status' | 'employment_type' | 'work_area' | 'compensation_type' | 'boolean';
}> {
  return group.fields.map((field) => {
    const base = { label: field.label, key: field.key };
    if (field.kind === 'date') return { ...base, type: 'date' as const };
    if (field.kind === 'number') return { ...base, type: 'number' as const };
    if (field.kind === 'text') return base;
    return { ...base, kind: field.kind };
  });
}

function parseExportNumber(value: unknown): number {
  const raw = String(value ?? '').trim();
  if (!raw) return Number.NaN;
  const parsed = Number.parseFloat(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function formatExportNumber(value: unknown, key: StammdatenExportFieldKey): string {
  const parsed = parseExportNumber(value);
  if (!Number.isFinite(parsed)) return '';
  if (parsed === 0 && ZERO_MEANS_EMPTY_NUMBER_FIELDS.has(key)) return '';
  return parsed.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

function formatExportDate(value: unknown): string {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('de-DE');
}

function formatExportIban(value: unknown): string {
  const cleaned = String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  if (!cleaned) return '';
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? cleaned;
}

function labelForEnum(
  kind: 'tax_class' | 'religion' | 'marital_status' | 'employment_type' | 'work_area' | 'compensation_type',
  value: unknown,
): string {
  const raw = String(value ?? '');
  const options =
    kind === 'tax_class'
      ? TAX_CLASS_OPTIONS
      : kind === 'religion'
        ? RELIGION_OPTIONS
        : kind === 'marital_status'
          ? MARITAL_STATUS_OPTIONS
          : kind === 'work_area'
            ? WORK_AREA_OPTIONS
            : kind === 'compensation_type'
              ? COMPENSATION_TYPE_OPTIONS
              : EMPLOYMENT_TYPE_OPTIONS;
  return options.find((opt) => opt.value === raw)?.label ?? raw;
}

export function formatEmployeeExportCell(row: EmployeeExportRow, field: StammdatenExportField): string {
  const raw = row[field.key];

  switch (field.kind) {
    case 'date':
      return formatExportDate(raw);
    case 'number':
      return formatExportNumber(raw, field.key);
    case 'license':
      return Array.isArray(raw) ? raw.map(String).filter(Boolean).join(', ') : '';
    case 'boolean':
      return Boolean(raw) ? 'Ja' : 'Nein';
    case 'tax_class':
    case 'religion':
    case 'marital_status':
    case 'employment_type':
    case 'work_area':
    case 'compensation_type':
      return labelForEnum(field.kind, raw);
    case 'text':
      if (field.key === 'iban') return formatExportIban(raw);
      return String(raw ?? '').trim();
    default:
      return String(raw ?? '').trim();
  }
}

export function resolveExportFields(fieldKeys: StammdatenExportFieldKey[]): StammdatenExportField[] {
  const resolved: StammdatenExportField[] = [];
  const seen = new Set<string>();
  for (const key of fieldKeys) {
    if (seen.has(key)) continue;
    const field = FIELD_BY_KEY.get(key);
    if (!field) continue;
    seen.add(key);
    resolved.push(field);
  }
  return resolved;
}

export function orderExportFieldKeys(selectedKeys: Iterable<string>): StammdatenExportFieldKey[] {
  const selected = new Set(selectedKeys);
  const ordered: StammdatenExportFieldKey[] = [];
  for (const key of ALL_STAMMDATEN_EXPORT_FIELD_KEYS) {
    if (selected.has(key)) ordered.push(key);
  }
  return ordered;
}

export function buildEmployeeStammdatenCsv(
  rows: EmployeeExportRow[],
  fields: StammdatenExportField[],
): string {
  const headerRow = fields.map((f) => f.label);
  const dataRows = rows.map((row) => fields.map((field) => formatEmployeeExportCell(row, field)));
  return buildCsvDocument(headerRow, dataRows);
}

export function buildEmployeeDataExportParams(
  employeeIds: string[],
  allEmployeeIds: string[],
  fieldKeys: StammdatenExportFieldKey[],
): URLSearchParams {
  const params = new URLSearchParams();
  const isAll =
    employeeIds.length === 0 ||
    (allEmployeeIds.length > 0 && employeeIds.length >= allEmployeeIds.length);
  if (!isAll && employeeIds.length > 0) {
    params.set('employeeIds', employeeIds.join(','));
  }
  if (fieldKeys.length > 0) {
    params.set('fields', fieldKeys.join(','));
  }
  return params;
}
